import React from 'react';
import { Navigate } from 'react-router-dom';

import { useProfile } from '../Components/Hooks/UserHooks';
import PublicLayout from '../Layouts/PublicLayout';
import PageNotFound from '../pages/Error/Cover404notfound';
import HyrocLanding from '../pages/PublicTenderAnnouncement/Landing';
import { PublicLanguageProvider } from '../pages/PublicTenderAnnouncement/i18n/LanguageContext';
import PublicTenderAnnouncement from '../pages/PublicTenderAnnouncement';
import PublicTenderAnnouncementDetail from '../pages/PublicTenderAnnouncement/Detail';
import PublicPrivacyPolicy from '../pages/PublicTenderAnnouncement/PrivacyPolicy';
import { isPublicLandingEnabled } from '../pages/PublicTenderAnnouncement/services/publicLandingFlag';

/**
 * Titik masuk route publik.
 *
 * Layout dipasang di dalam komponen ini (bukan di konfigurasi route) supaya
 * saat perlu mengalihkan halaman, navbar publik tidak sempat berkedip lebih
 * dulu.
 *
 * Catatan `useProfile`: hook itu memulai `loading` dengan nilai true lalu
 * menyelesaikannya di useEffect, tetapi `token` sudah terisi benar pada render
 * pertama. Karena itu keputusan arah TIDAK menunggu `loading` selesai — kalau
 * ditunggu, halaman akan berkedip.
 */

const withPublicLayout = (children, showBackHome = false) => (
  <PublicLayout showBackHome={showBackHome}>{children}</PublicLayout>
);

/**
 * Route `/` — landing HYROC (D-26).
 *
 * Sebelumnya halaman ini menampilkan daftar pengumuman; daftar itu kini hanya
 * di `/tender-announcement`. Aturan pengalihan tidak berubah.
 *
 * `Landing` sengaja TIDAK dibungkus PublicLayout — rancangannya foto penuh
 * layar tanpa navbar dan footer.
 */
const RootEntry = () => {
  const { token } = useProfile();

  if (token) return <Navigate to='/dashboard' replace />;
  if (!isPublicLandingEnabled()) return <Navigate to='/login' replace />;

  // Provider bahasa dipasang di sini karena Landing tidak memakai PublicLayout
  // (yang biasanya menyediakannya). Tanpa ini `usePublicLanguage()` jatuh ke
  // nilai bawaan dan tombol ID/EN diam-diam tidak berfungsi.
  return (
    <PublicLanguageProvider>
      <HyrocLanding />
    </PublicLanguageProvider>
  );
};

/** Route `/tender-announcement` — alamat kanonik halaman list. */
const TenderAnnouncementEntry = () => {
  if (!isPublicLandingEnabled()) return <Navigate to='/login' replace />;
  return withPublicLayout(<PublicTenderAnnouncement />, true);
};

/** Route `/tender-announcement/:id` — halaman detail. */
const TenderAnnouncementDetailEntry = () => {
  if (!isPublicLandingEnabled()) return <Navigate to='/login' replace />;
  return withPublicLayout(<PublicTenderAnnouncementDetail />);
};

/**
 * Route `/privacy-policy` — kebijakan privasi khusus halaman publik.
 * Terpisah dari `/pages-privacy-policy` (halaman template Velzon di area
 * ber-login) yang isinya bukan kebijakan Pertamina.
 */
const PrivacyPolicyEntry = () => {
  if (!isPublicLandingEnabled()) return <Navigate to='/login' replace />;
  return withPublicLayout(<PublicPrivacyPolicy />);
};

/**
 * Route `*`. Sebelumnya semua path tak dikenal dilempar ke `/dashboard`,
 * yang bagi pengunjung publik berujung di `/login` dan terasa seperti
 * aplikasi rusak.
 */
const NotFoundEntry = () => {
  const { token } = useProfile();

  if (token) return <Navigate to='/dashboard' replace />;
  return <PageNotFound />;
};

export {
  RootEntry,
  TenderAnnouncementEntry,
  TenderAnnouncementDetailEntry,
  PrivacyPolicyEntry,
  NotFoundEntry,
};
